import React, {useContext} from 'react'
import DesignSystemContent from '../../components/templates/designSystem/DesignSystemContent'
import { AccentTextM, AccentTextS, ArticleH2, BoldTextXS, TextXS } from './Fonts'
import Grid from '../../components/molecules/Grid'
import DesignSystemItem from '../../components/templates/designSystem/DesignSystemItem'
import styled, {ThemeContext} from 'styled-components'
import {basicColor, gradient, radius, testBackground} from '../../styles/template'

interface Props {
}

const ColorSwatchWrap = styled(DesignSystemItem)`
    padding: 0;
    grid-template-rows: 15rem 1fr;
`
const ColorSwatch = styled.div`
    width: 100%;
    height: 100%;
    border-radius: ${radius.large};
`

const ColorSwatchTextWrap = styled.div`
    padding: 0 4rem 4rem;
    display: grid;
    grid-template-rows: auto;
    gap: 2rem;
    place-items: center;
    align-items: start;
    text-align: center;
`

const Colors: React.FC<Props> = () => {
    const { title: themeTitle } = useContext(ThemeContext);

    return (
        <DesignSystemContent title='Colors'>
            <code>{"${props => props.theme.color.Item}"}</code>
            {colorsData.map((item: any, index: any) => {
                return <Grid col='1' gap='2' key={index}>
                    <ArticleH2>{item.palette}</ArticleH2>
                    <Grid col='1' colDesktop='4' gap='4'>
                        {item.swatches.map((swatch: any, index: any) => {
                            let color, description;
                            color = swatch.colorCode;
                            description = swatch.description

                            if (themeTitle === 'dark' && swatch.darkThemeColor) {
                                color =  swatch.darkThemeColor
                            }

                            if (themeTitle === 'dark' && swatch.darkThemeDescription) {
                                description = swatch.darkThemeDescription
                            }

                            return <ColorSwatchWrap key={index}>
                                <ColorSwatch style={{background: color}} />
                                <ColorSwatchTextWrap>
                                    <BoldTextXS><code>{swatch.title}</code></BoldTextXS>
                                    <TextXS>{description}</TextXS>
                                </ColorSwatchTextWrap>
                            </ColorSwatchWrap>
                        })}
                    </Grid>
                </Grid>

            })}

        </DesignSystemContent>
    )
}

const colorsData = [
    {
        palette: 'Grayscale',
        swatches: [
            {
                title: '${basicColor.white}',
                description: 'Used for background, test text and icons',
                darkThemeDescription: 'Used for body text, titles, default icons',
                colorCode: `${basicColor.white}`,
            },
            {
                title: '${basicColor.lightGrey}',
                description: 'Used for background',
                darkThemeDescription: '---',
                colorCode: `${basicColor.lightGrey}`,
            },
            {
                title: '${basicColor.faintGray}',
                description: 'Used for background, sort items, accordions',
                darkThemeDescription: '---',
                colorCode: `${basicColor.faintGray}`
            },
            {
                title: '${basicColor.transparent}',
                description: 'Transparency elements above backgrounds',
                darkThemeDescription: 'Used for dark elements with opacity',
                colorCode: `${basicColor.transparent}`
            },

            {
                title: 'Text',
                description: 'Used for some iconst, menus, text',
                darkThemeDescription: 'Used for dates, small captions',
                colorCode: `${basicColor.black}`,
                darkThemeColor: `${basicColor.faintGray}`,
            },

            {
                title: 'LightItem',
                description: 'Used for some iconst, menus, text',
                darkThemeDescription: 'Used for dates, small captions',
                colorCode: `${basicColor.faintGray}`,
                darkThemeColor: `${basicColor.midGrayDarkTheme}`,
            },
            {
                title: 'MidtoneItem',
                description: 'Used for icons, descriptions',
                darkThemeDescription: 'Used for disabled buttons and links',
                colorCode: `${basicColor.midGray}`,
                darkThemeColor: `${basicColor.darkGrayDarkTheme}`,
            },
            {
                title: 'Item',
                description: 'Used for body text, headlines',
                darkThemeDescription: 'Used for items backgrounds',
                colorCode: `${basicColor.white}`,
                darkThemeColor: `${basicColor.blackDarkTheme}`,
            },
            {
                title: 'Background',
                description: 'Used for menus, items, illustrations',
                darkThemeDescription: 'Used for app background',
                colorCode: `${basicColor.lightGrey}`,
                darkThemeColor: `${basicColor.deepDarkDarkTheme}`,
            },
        ]
    },
    {
        palette: 'Basic',
        swatches: [
            {
                title: '${basicColor.primary}',
                description: 'Used as the primary color',
                colorCode: `${basicColor.primary}`
            },
            {
                title: '${basicColor.secondary}',
                description: 'Used for right indicators, as secondary color in illustrations',
                colorCode: `${basicColor.secondary}`
            },
            {
                title: '${basicColor.accent}',
                description: 'Used as accent color, for some icons',
                colorCode: `${basicColor.accent}`
            },
            {
                title: '${basicColor.red}',
                description: 'Used for buttons, errors, negative answers, likes',
                colorCode: `${basicColor.red}`
            },
            {
                title: '${basicColor.primaryLight}',
                description: 'Used for some buttons and states',
                colorCode: `${basicColor.primaryLight}`
            },
            {
                title: '${basicColor.primaryTransparent}',
                description: 'Used for some buttons and states',
                colorCode: `${basicColor.primaryTransparent}`
            },
        ]
    },
    {
        palette: 'Gradients',
        swatches: [
            {
                title: '${gradient.primary}',
                description: 'Used for about, backgrounds',
                colorCode: `${gradient.primary}`
            },
            {
                title: '${gradient.green}',
                description: 'Used for cards in slider',
                colorCode: `${gradient.green}`
            },
            {
                title: '${gradient.orange}',
                description: 'Used for cards in slider',
                colorCode: `${gradient.orange}`
            },
            {
                title: '${gradient.purple}',
                description: 'Used for cards in slider',
                colorCode: `${gradient.purple}`
            },
        ]
    },
    {
        palette: 'Tests backgrounds',
        swatches: [
            {
                title: '${testBackground.primary}',
                description: 'Used as the most common background for tests',
                colorCode: `${testBackground.primary}`
            },
            {
                title: '${testBackground.secondary}',
                description: 'Used as a second common background for test',
                colorCode: `${testBackground.secondary}`
            },
            {
                title: '${testBackground.brightDark}',
                description: 'Used for some tests with images on specific background',
                colorCode: `${testBackground.brightDark}`
            },
            {
                title: '${testBackground.darkBlue}',
                description: 'Used for specific tests like IQ',
                colorCode: `${testBackground.darkBlue}`
            },
            {
                title: '${testBackground.brightGreen}',
                description: 'Used fo child tests',
                colorCode: `${testBackground.brightGreen}`
            },
            {
                title: '${testBackground.neutral}',
                description: 'Used when color can interfere (for example for Rorschach)',
                colorCode: `${testBackground.neutral}`
            },
        ]
    },
];

export default Colors